<template>
  <!-- The picker's menu must escape the body only while nothing sits under the picker; once the steps show, the body scrolls -->
  <Modal ref="modalRef" :content-class="receiver ? undefined : 'team-picker-dialog'" width="32rem">
    <template #header>Remove {{ name }} from the team</template>

    <template #body>
      <View direction="column" gap="md" class="form">
        <template v-if="holdsAnything">
          <Text variant="label-medium">Hand their properties and portfolios to</Text>
          <TeamMemberPicker v-model="transferTo" :members="members" :exclude-ids="[member?.investorId]" />
        </template>

        <View v-if="receiver" direction="column" gap="sm">
          <Text variant="h4">What happens after you remove {{ name }}:</Text>

          <Card variant="outline" size="medium">
            <View direction="column" gap="md">
              <View v-for="step in steps" :key="step.title" gap="md">
                <IconTile size="small" color="orange" :icon="step.icon" />

                <View direction="column" gap="xs">
                  <Text variant="h5">{{ step.title }}</Text>
                  <Text variant="body-medium" color="secondary">{{ step.description }}</Text>
                </View>
              </View>
            </View>
          </Card>
        </View>
      </View>
    </template>

    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button appearance="danger" :loading="isSubmitting" :disabled="!receiver" @click="confirm">Remove member</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed, ref } from 'vue';

import { Button, Card, IconName, IconTile, Modal, Text, View, useAlertsStore } from '@tallo/design-system';
import {
  CompanyTeamMemberDto,
  TeamMemberPicker,
  seatHoldings,
  teamMemberName as nameOf,
  useCompanyPortfoliosQuery,
  useCompanyTeamQuery,
  useRemoveMember,
} from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import {
  buildingQueryKey,
  buildingsQueryKey,
  isPropertyTeamQuery,
  portfolioQueryKey,
  useBuildingsQuery,
  usePortfolioQuery,
} from '@tallo/investor/property';
import { showingAgentsQueryKey } from '@tallo/investor/showing-agents';
import { escapeHtml } from '@tallo/utility';

const modalRef = ref<InstanceType<typeof Modal>>();
const member = ref<CompanyTeamMemberDto>();
const transferTo = ref<string>();

const alertsStore = useAlertsStore();
const queryCache = useQueryCache();
const { investor } = storeToRefs(useInvestorStore());
const { members } = useCompanyTeamQuery();
const { properties, data: propertiesPage } = usePortfolioQuery();
const { portfolios } = useCompanyPortfoliosQuery();
const { buildings } = useBuildingsQuery();
const removeMember = useRemoveMember();
const isSubmitting = removeMember.isLoading;

const name = computed(() => (member.value ? nameOf(member.value) : ''));

const holdings = computed(() =>
  member.value
    ? seatHoldings(
        member.value.investorId,
        properties.value,
        portfolios.value,
        (propertiesPage.value?.totalPages ?? 1) > 1,
      )
    : undefined,
);

// Buildings change hands with the seat's units; they come from their own list, not the property page
const managedBuildingCount = computed(
  () => buildings.value.filter((building) => building.manager?.investorId === member.value?.investorId).length,
);

// Nothing to hand over means nothing to pick; the removal still names you as the receiver
const holdsAnything = computed(
  () =>
    Boolean(holdings.value?.managedUnitCount || holdings.value?.ownedPortfolios.length || managedBuildingCount.value) ||
    holdings.value?.isPartial,
);

const receiver = computed(() => members.value.find((seat) => seat.investorId === transferTo.value));

const steps = computed(() => {
  if (!holdings.value || !receiver.value) return [];

  const to = receiver.value.isYou ? 'You' : nameOf(receiver.value);
  const unitsLabel = holdings.value.isPartial
    ? 'every property they manage'
    : pluralize('property', holdings.value.managedUnitCount, true);
  const list: { icon: IconName; title: string; description: string }[] = [];

  if (holdings.value.managedUnitCount || holdings.value.isPartial) {
    list.push({
      icon: 'bell-01',
      title: `${to} ${receiver.value.isYou ? 'manage' : 'manages'} ${unitsLabel}`,
      description:
        'New showing requests, renter questions and queued reminders go to them, and they join those properties as a showing agent.',
    });
  }

  if (managedBuildingCount.value) {
    list.push({
      icon: 'building-06',
      title: `${to} ${receiver.value.isYou ? 'manage' : 'manages'} ${pluralize('building', managedBuildingCount.value, true)}`,
      description: 'They join those buildings as a showing agent.',
    });
  }

  if (holdings.value.ownedPortfolios.length) {
    list.push({
      icon: 'folder',
      title: `${to} ${receiver.value.isYou ? 'own' : 'owns'} ${holdings.value.ownedPortfolios.map((p) => p.name).join(', ')}`,
      description: `${to} ${receiver.value.isYou ? 'manage' : 'manages'} every property in ${holdings.value.ownedPortfolios.length === 1 ? 'it' : 'them'}; anyone else who managed one stays on it as a collaborator.`,
    });
  }

  list.push(
    {
      icon: 'calendar',
      title: `${name.value} stops hosting tours`,
      description:
        "The upcoming tours they host move to each property's manager (a manager with no phone number leaves the tour for your team to host), and they come off every property's showing agents.",
    },
    {
      icon: 'users-01',
      title: `${name.value} comes off every property and portfolio they collaborate on`,
      description: 'Their name stays on past activity.',
    },
    {
      icon: 'log-out-03',
      title: `${name.value} is signed out everywhere`,
      description: "They can't sign in again unless you invite them back. They aren't emailed about it.",
    },
  );

  return list;
});

function open(target: CompanyTeamMemberDto) {
  member.value = target;
  transferTo.value = holdsAnything.value ? undefined : investor.value?.id;
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

async function confirm() {
  if (!member.value || !transferTo.value) return;

  const removed = name.value;

  try {
    await removeMember.mutateAsync({ member: member.value, transferTo: transferTo.value });
    // The company lib refreshed the roster and the portfolios; the units, buildings, their teams and agents moved too
    queryCache.invalidateQueries({ key: [portfolioQueryKey] }).catch(() => undefined);
    queryCache.invalidateQueries({ key: [buildingsQueryKey] }).catch(() => undefined);
    queryCache.invalidateQueries({ key: [buildingQueryKey] }).catch(() => undefined);
    queryCache.invalidateQueries({ key: [showingAgentsQueryKey] }).catch(() => undefined);
    queryCache.invalidateQueries({ predicate: isPropertyTeamQuery }).catch(() => undefined);
    close();
    alertsStore.success(`<b>${escapeHtml(removed)}</b> removed from the team`);
  } catch {
    // Already reported by the mutation's toast; the dialog stays open for another try
  }
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.form {
  min-width: 0;
}
</style>
